// ── Types ─────────────────────────────────────────────────────────────────────

import type { ScheduleEntry } from '../components/forms/AblaufplanEntryModal';

export type ScheduleEntryAssignment = {
  id: string;
  entry_id: string;
  assignment_type: 'all' | 'individual' | 'rule' | 'role' | 'user';
  submission_id: string | null;
  role_id: string | null;
  assigned_user_id: string | null;
  rule_field_key: string | null;
  rule_operator: 'contains' | 'equals' | 'starts_with' | 'in' | null;
  rule_value: string | null;
};

export type UserRole = { user_id: string; role_id: string };

export type FormSubmission = {
  id: string;
  form_id: string;
  user_id: string;
  participant_name?: string; // display name, optional
};

export type FormSubmissionAnswer = {
  submission_id: string;
  field_key: string;
  value_text: string | null;
  value_json: unknown;
};

export type ConflictSeverity = 'error' | 'warning';

export type ConflictType =
  | 'participant_overlap'
  | 'missing_assignment'
  | 'missing_submission'
  | 'missing_category';

export type ScheduleConflict = {
  severity: ConflictSeverity;
  type: ConflictType;
  participantId?: string;
  participantName?: string;
  entryIds: string[];
  message: string;
};

// ── Helpers ───────────────────────────────────────────────────────────────────

/**
 * Returns true when two time ranges strictly overlap.
 * Adjacent ranges (10:00–11:00 and 11:00–12:00) do NOT overlap.
 */
export function timeRangesOverlap(
  aStart: string,
  aEnd: string,
  bStart: string,
  bEnd: string,
): boolean {
  return aStart < bEnd && bStart < aEnd;
}

/**
 * Resolves the set of user IDs that are assigned to an entry.
 * Returns user_ids (auth.users.id) as the common denominator.
 */
export function resolveAssignmentParticipants(
  entry: ScheduleEntry,
  entryAssignments: ScheduleEntryAssignment[],
  allSubmissions: FormSubmission[],
  allAnswers: FormSubmissionAnswer[],
  allUserRoles: UserRole[] = [],
): Set<string> {
  const formSubmissions = allSubmissions.filter((s) => s.form_id === entry.form_id);
  const result = new Set<string>();

  for (const a of entryAssignments) {
    if (a.assignment_type === 'all') {
      formSubmissions.forEach((s) => result.add(s.user_id));

    } else if (a.assignment_type === 'individual' && a.submission_id) {
      const sub = allSubmissions.find((s) => s.id === a.submission_id);
      if (sub) result.add(sub.user_id);

    } else if (a.assignment_type === 'user' && a.assigned_user_id) {
      result.add(a.assigned_user_id);

    } else if (a.assignment_type === 'role' && a.role_id) {
      allUserRoles
        .filter((ur) => ur.role_id === a.role_id)
        .forEach((ur) => result.add(ur.user_id));

    } else if (a.assignment_type === 'rule' && a.rule_field_key && a.rule_value) {
      formSubmissions.forEach((s) => {
        const answer = allAnswers.find(
          (ans) => ans.submission_id === s.id && ans.field_key === a.rule_field_key,
        );
        if (!answer?.value_text) return;
        const text = answer.value_text;
        const val = a.rule_value!;
        const matches =
          a.rule_operator === 'equals'
            ? text === val
            : a.rule_operator === 'contains'
              ? text.toLowerCase().includes(val.toLowerCase())
              : a.rule_operator === 'starts_with'
                ? text.toLowerCase().startsWith(val.toLowerCase())
                : false;
        if (matches) result.add(s.user_id);
      });
    }
  }

  return result;
}

// ── Main conflict detector ────────────────────────────────────────────────────

export function findScheduleConflicts(
  entries: ScheduleEntry[],
  allAssignments: ScheduleEntryAssignment[],
  allSubmissions: FormSubmission[],
  allAnswers: FormSubmissionAnswer[],
  allUserRoles: UserRole[] = [],
): ScheduleConflict[] {
  const conflicts: ScheduleConflict[] = [];
  const assignmentsByEntry = new Map<string, ScheduleEntryAssignment[]>();
  for (const a of allAssignments) {
    const list = assignmentsByEntry.get(a.entry_id) ?? [];
    list.push(a);
    assignmentsByEntry.set(a.entry_id, list);
  }

  // Per-entry checks
  for (const entry of entries) {
    const ea = assignmentsByEntry.get(entry.id) ?? [];

    if (ea.length === 0) {
      conflicts.push({
        severity: 'warning',
        type: 'missing_assignment',
        entryIds: [entry.id],
        message: `„${entry.title}" hat keine Teilnehmerzuordnung und ist für niemanden sichtbar.`,
      });
    }

    if (!entry.category_id) {
      conflicts.push({
        severity: 'warning',
        type: 'missing_category',
        entryIds: [entry.id],
        message: `„${entry.title}" hat keine Kategorie zugewiesen.`,
      });
    }

    for (const a of ea) {
      if (a.assignment_type === 'individual' && a.submission_id) {
        if (!allSubmissions.find((s) => s.id === a.submission_id)) {
          conflicts.push({
            severity: 'error',
            type: 'missing_submission',
            entryIds: [entry.id],
            message: `„${entry.title}": Eine Einzelzuordnung verweist auf eine nicht mehr existierende Anmeldung.`,
          });
        }
      }
    }
  }

  // Pairwise overlap checks (same day only)
  const participantsCache = new Map<string, Set<string>>();
  const getParticipants = (entry: ScheduleEntry) => {
    if (!participantsCache.has(entry.id)) {
      participantsCache.set(
        entry.id,
        resolveAssignmentParticipants(
          entry,
          assignmentsByEntry.get(entry.id) ?? [],
          allSubmissions,
          allAnswers,
          allUserRoles,
        ),
      );
    }
    return participantsCache.get(entry.id)!;
  };

  const reportedPairs = new Set<string>();

  for (let i = 0; i < entries.length; i++) {
    for (let j = i + 1; j < entries.length; j++) {
      const a = entries[i];
      const b = entries[j];
      if (a.entry_date !== b.entry_date) continue;
      if (!timeRangesOverlap(a.start_time, a.end_time, b.start_time, b.end_time)) continue;

      const aParticipants = getParticipants(a);
      const bParticipants = getParticipants(b);

      for (const pid of aParticipants) {
        if (!bParticipants.has(pid)) continue;
        const pairKey = [a.id, b.id, pid].sort().join('|');
        if (reportedPairs.has(pairKey)) continue;
        reportedPairs.add(pairKey);

        const sub = allSubmissions.find((s) => s.user_id === pid);
        conflicts.push({
          severity: 'error',
          type: 'participant_overlap',
          participantId: pid,
          participantName: sub?.participant_name ?? pid.slice(0, 8),
          entryIds: [a.id, b.id],
          message: `${sub?.participant_name ?? 'Ein Teilnehmer'} ist gleichzeitig in „${a.title}" (${a.start_time.slice(0, 5)}–${a.end_time.slice(0, 5)}) und „${b.title}" (${b.start_time.slice(0, 5)}–${b.end_time.slice(0, 5)}) eingeplant.`,
        });
        break;
      }
    }
  }

  return conflicts;
}
